import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { X, ChevronRight } from "lucide-react";
import { Button } from "@/component/ui/button";
import { cn } from "@/utils/utils";
import {
  getSurveyQuestions,
  getResponseOptions,
  createSurveyAttempt,
  getLatestSurveyAttempt,
  saveSurveyResponses,
  SurveyQuestion,
  ResponseOption,
  SurveyResponse,
} from "@/services/survey";
import { useSidebarStore } from "../store/sidebare-store";
import { ConfirmModal } from "../ui/confirmation";
import { handleConfirmNavigation } from "../ui/confirm-navigation-popup";
import { PageContainer } from "@/component/layout";

interface SurveyQuestionPageProps {
  userName: string;
  studentId: string;
}

export const SurveyQuestionPage = ({ studentId }: SurveyQuestionPageProps) => {
  const navigate = useNavigate();
  const { questionNumber } = useParams<{ questionNumber: string }>();

  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [responseOptions, setResponseOptions] = useState<ResponseOption[]>([]);
  const [currentResponses, setCurrentResponses] = useState<
    Record<number, number>
  >({});
  const [surveyId, setSurveyId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const pageNumber = parseInt(questionNumber || "1", 10);

  useEffect(() => {
    initializeSurvey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (pageNumber !== currentPage) {
      // Guard against URL manipulation within the same session.
      // Read from localStorage directly to avoid stale closure issues.
      const savedProgress = (() => {
        try {
          const saved = localStorage.getItem("surveyProgress");
          if (!saved) return null;
          const p = JSON.parse(saved);
          if (
            typeof p.surveyId === "number" &&
            typeof p.maxReachedPage === "number"
          )
            return p;
          return null;
        } catch {
          return null;
        }
      })();
      const maxAllowedPage = savedProgress?.maxReachedPage ?? 1;
      if (pageNumber > maxAllowedPage) {
        navigate(`/student/survey/question/${maxAllowedPage}`, {
          replace: true,
        });
        return;
      }
      loadQuestionsPage(pageNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber]);

  useEffect(() => {
    (async () => {
      // Disable sidebar by default
      useSidebarStore.getState().setSidebarState({
        surveyResult: false,
      });
    })();
  }, []);

  const getStoredProgress = (): {
    surveyId: number;
    maxReachedPage: number;
  } | null => {
    try {
      const saved = localStorage.getItem("surveyProgress");
      if (!saved) return null;
      const p = JSON.parse(saved);
      if (typeof p.surveyId === "number" && typeof p.maxReachedPage === "number")
        return p;
      return null;
    } catch {
      return null;
    }
  };

  const initializeSurvey = async () => {
    try {
      setLoading(true);

      // reusing getLatestSurveyAttempt, if the student already has a completed attempt, then user redirect to results
      try {
        const existingAttempt = await getLatestSurveyAttempt(studentId);
        if (existingAttempt.surveyId) {
          localStorage.removeItem("surveyProgress");
          useSidebarStore.getState().setSidebarState({ surveyResult: true });
          localStorage.setItem(
            "currentSurveyId",
            String(existingAttempt.surveyId),
          );
          navigate(
            `/student/survey/results?surveyId=${existingAttempt.surveyId}`,
            { replace: true },
          );
          return;
        }
      } catch {
        // No completed attempt, then proceed to start the survey
      }

      const optionsResponse = await getResponseOptions();
      setResponseOptions(optionsResponse.responseOptions);

      // Resume an existing in-progress attempt from localStorage instead of
      // creating a new one every time the component mounts.
      const savedProgress = getStoredProgress();
      let currentSurveyId: number;
      let maxReachedPage: number;

      if (savedProgress) {
        currentSurveyId = savedProgress.surveyId;
        maxReachedPage = savedProgress.maxReachedPage;
      } else {
        const attempt = await createSurveyAttempt(studentId);
        currentSurveyId = attempt.surveyId;
        maxReachedPage = 1;
        localStorage.setItem(
          "surveyProgress",
          JSON.stringify({ surveyId: currentSurveyId, maxReachedPage: 1 }),
        );
      }

      setSurveyId(currentSurveyId);

      // Enforce sequential navigation: never let the user land on a page
      // they haven't legitimately reached yet.
      const targetPage =
        pageNumber > maxReachedPage ? maxReachedPage : pageNumber;
      if (pageNumber > maxReachedPage) {
        navigate(`/student/survey/question/${maxReachedPage}`, {
          replace: true,
        });
      }

      await loadQuestionsPage(targetPage);
    } catch (err: any) {
      setError(err.message || "Failed to initialize survey");
    } finally {
      setLoading(false);
    }
  };

  const loadQuestionsPage = async (page: number) => {
    try {
      const questionsResponse = await getSurveyQuestions(page, 5);

      // Validate page number against total pages
      const { currentPage: responsePage, totalPages: responseTotalPages } =
        questionsResponse.pagination;

      // If the requested page is invalid, show error
      if (page < 1) {
        setError("Invalid page number. Please start from page 1.");
        return;
      }

      if (page > responseTotalPages && responseTotalPages > 0) {
        setError(
          `Page ${page} does not exist. There are only ${responseTotalPages} pages available.`,
        );
        return;
      }

      // If no questions returned, it's an invalid page
      if (
        !questionsResponse.questions ||
        questionsResponse.questions.length === 0
      ) {
        setError(`No questions found for page ${page}.`);
        return;
      }

      setQuestions(questionsResponse.questions);
      setCurrentPage(responsePage);
      setTotalPages(responseTotalPages);
    } catch (err: any) {
      setError(err.message || "Failed to load questions");
    }
  };

  const handleResponseChange = (
    questionId: number,
    responseOptionId: number,
  ) => {
    setCurrentResponses((prev) => ({
      ...prev,
      [questionId]: responseOptionId,
    }));
  };

  const handleNext = async () => {
    if (!surveyId) return;

    try {
      const responses: SurveyResponse[] = questions
        .filter((q) => currentResponses[q.questionId])
        .map((q) => ({
          questionId: q.questionId,
          responseOptionId: currentResponses[q.questionId],
        }));

      if (responses.length > 0) {
        await saveSurveyResponses(surveyId, responses);
      }

      if (currentPage < totalPages) {
        const nextPage = currentPage + 1;
        // Unlock the next page before navigating to it.
        try {
          const saved = localStorage.getItem("surveyProgress");
          const progress = saved
            ? JSON.parse(saved)
            : { surveyId, maxReachedPage: 1 };
          localStorage.setItem(
            "surveyProgress",
            JSON.stringify({
              surveyId: progress.surveyId,
              maxReachedPage: Math.max(progress.maxReachedPage ?? 1, nextPage),
            }),
          );
        } catch { /* non-fatal */ }
        navigate(`/student/survey/question/${nextPage}`);
      } else {
        // Survey complete — clear in-progress tracking and store final id.
        localStorage.removeItem("surveyProgress");
        localStorage.setItem("currentSurveyId", String(surveyId));
        useSidebarStore.getState().setSidebarState({ surveyResult: true });
        navigate(`/student/survey/results?surveyId=${surveyId}`);
      }

      //Scroll the user to the top of the page
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err: any) {
      setError(err.message || "Failed to save responses");
    }
  };

  const [showComplete, setShowComplete] = useState(false);

  const exitSurvey = () => handleConfirmNavigation(navigate, "/student");

  const allQuestionsAnswered = questions.every(
    (q) => currentResponses[q.questionId],
  );
  const completedPages =
    Math.max(0, currentPage - 1) + (allQuestionsAnswered ? 1 : 0);
  const progressPercentage = Math.round((completedPages / totalPages) * 100);

  if (loading) {
    return (
      <div className="flex-1 min-h-screen flex items-center justify-center">
        <div className="text-center">Loading survey...</div>
      </div>
    );
  }

  if (error) {
    return (
      <PageContainer className="flex-1 min-h-screen flex items-center justify-center">
        <div className="text-center text-[var(--destructive)]">
          <p>Error: {error}</p>
          <Button onClick={() => navigate("/student")} className="mt-4">
            Return to Dashboard
          </Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <div className="flex-1 min-h-screen bg-[var(--header)]">
      {/* Header */}
      <div className="w-full px-4 md:px-6 py-4 md:py-5">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={exitSurvey}
            className="flex items-center gap-2 text-[var(--color-grey-600)] hover:text-[var(--foreground)] transition-colors"
          >
            <X className="w-4 h-4 md:w-5 md:h-5" />
            <span className="text-sm md:text-base font-semibold">
              Exit Questionnaire
            </span>
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="w-full px-4 md:px-6 pb-4 md:pb-6">
        <div className="max-w-md mx-auto">
          <div className="w-full h-2 rounded-full bg-[var(--color-grey-200)]">
            <div
              className="h-2 rounded-full bg-[var(--primary)] transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <p className="text-sm text-center text-[var(--color-grey-600)] mt-2">
            {progressPercentage}% complete
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="w-full px-4 md:px-6">
        <div className="max-w-6xl mx-auto pb-20 md:pb-24">
          {/* Instructions */}
          <section className="py-3 md:py-4">
            <p className="text-sm md:text-base text-[var(--foreground)] font-bold md:font-normal text-center md:text-left">
              Rate each item on a scale of 1–5 scale:
            </p>
            <p className="text-xs md:text-sm text-[var(--foreground)] font-normal text-center md:text-left mt-0.5">
              1 = Strongly Disagree | 2 = Disagree | 3 = Neutral | 4 = Agree | 5
              = Strongly Agree
            </p>
          </section>

          {/* Questions */}
          <section className="mt-6 md:mt-8 space-y-8 md:space-y-12">
            {questions.map((question) => (
              <div key={question.questionId} className="w-full">
                <h3 className="text-base md:text-lg font-semibold text-center leading-relaxed px-4 mb-6 md:mb-8">
                  {question.questionText}
                </h3>

                {/* Rating buttons */}
                <div className="flex flex-wrap justify-center gap-2 md:gap-4">
                  {responseOptions.map((option) => {
                    const selected =
                      currentResponses[question.questionId] ===
                      option.responseOptionId;
                    return (
                      <button
                        key={option.responseOptionId}
                        onClick={() =>
                          handleResponseChange(
                            question.questionId,
                            option.responseOptionId,
                          )
                        }
                        aria-pressed={selected}
                        aria-label={`Rate ${option.responseOptionScore} out of 5`}
                        className={cn(
                          "min-w-[60px] md:min-w-[100px]",
                          "flex-1 max-w-[70px] md:max-w-[109px]",
                          "h-12 md:h-16",
                          "flex items-center justify-center",
                          "rounded-xl border-2",
                          "text-lg md:text-2xl",
                          "transition-all duration-150",
                          "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]",
                          "disabled:opacity-50 disabled:cursor-not-allowed",
                          selected
                            ? "bg-[var(--color-deep-blue-50)] border-[var(--primary)] text-[var(--primary)] font-semibold shadow-sm"
                            : "bg-white border-gray-300 text-[var(--foreground)] font-medium hover:border-[var(--primary)] hover:shadow-sm",
                        )}
                      >
                        {option.responseOptionScore}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </section>

          {/* Footer / Navigation */}
          <footer className="mt-12 md:mt-16 text-center">
            <p className="text-sm md:text-base text-[var(--color-grey-600)] font-medium">
              Step {currentPage} of {totalPages}
            </p>

            {/* Page dots */}
            <div className="mt-3 md:mt-4 flex items-center justify-center gap-2">
              {Array.from({ length: totalPages }, (_, i) => (
                <span
                  key={i}
                  className={cn(
                    "h-2 w-2 rounded-full transition-colors",
                    i + 1 === currentPage
                      ? "bg-[var(--primary)]"
                      : "bg-[var(--color-grey-300)]",
                  )}
                />
              ))}
            </div>

            {/* CTA buttons */}
            <div className="mt-6 md:mt-8 flex flex-col md:flex-row justify-center gap-3 px-4">
              <Button
                variant="violetOutline"
                size="cta"
                onClick={() =>
                  navigate(
                    `/student/survey/question/${Math.max(1, currentPage - 1)}`,
                  )
                }
                disabled={currentPage === 1}
                className="w-full md:w-auto min-w-[140px]"
              >
                Previous
              </Button>
              <Button
                variant="primary"
                size="cta"
                onClick={
                  currentPage < totalPages
                    ? handleNext
                    : () => setShowComplete(true)
                }
                disabled={!allQuestionsAnswered}
                className="disabled:cursor-not-allowed w-full md:w-auto min-w-[140px]"
              >
                {currentPage < totalPages ? "Next" : "Complete Survey"}
                <ChevronRight className="ml-2" />
              </Button>
            </div>
          </footer>
        </div>
      </main>
      {/* Complete survey confirmation dialog */}
      {showComplete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="relative inline-flex flex-col items-end gap-5 bg-white rounded-2xl shadow-2xl pt-11 pb-11 px-6 w-[509px] max-w-[calc(100vw-2rem)]">
            <div className="flex flex-col items-center gap-[32px] w-full">
              <p className="text-center text-lg leading-7 font-normal text-[var(--color-curtin-primary-black-90)] [font-family:var(--font-proxima-nova)]">
                Thank you for completing the questionnaire.
                <br />
                You&apos;re results will be available anytime you log in.
              </p>
              <button
                onClick={handleNext}
                className="text-white text-lg leading-relaxed px-8 py-3 rounded-[5px] bg-[var(--primary)] hover:opacity-90 [font-family:var(--font-proxima-nova)]"
              >
                See my results
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal />
    </div>
  );
};
